import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import { DataGridPro, useGridApiRef, GridRow } from '@mui/x-data-grid-pro';
import { usePersistedDataStore } from '../../store.js';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from '../../../common/toast';
import { useUser } from '../../UserContext.js';
import { createOfferContext, resolveCourtDateForContext } from '../../../utils/offerContextUtils';
import {
  isFirstTownPortalResponseInSession,
  linkSessionFromUser,
  maybeNotifyTownPortalFirstResponse,
} from '../../../utils/townPortalFirstResponseNotify.js';
import { negotiatorIdForSettlement } from '../Settlement/negotiatorLookup.js';
import DoNotFileScarDialog from './components/DoNotFileScarDialog.js';
import { useNYData } from '../../../common/NyDataObject.js';
import logger from '../../../utils/logger';
import {
  createEmptyRowSelection,
  createRowSelectionFromIds,
  getSelectedRowIds,
} from '../utils/gridRowSelectionModel.js';
import { withSelectAllAwareCheckbox } from '../utils/gridSelectAllCheckbox.js';
// Extracted modules
// import { useCaseData } from './hooks/useCaseData.js';
// DataGrid keys + visibility + defaults: `./config/newColumns.js` (spreadsheet/XLS helpers stay in columns.js elsewhere).
import { getKeysForView, getDynamicColumnVisibility, INTERNAL_COLUMN_VISIBILITY, TOWN_COLUMN_VISIBILITY, DEFAULT_TABLE_PROPS } from './config/newColumns.js';
import { useColumnLayout } from '../config/columnVisibility.js';
import { createColumnDefinitions, getColumnPresenceFlags } from './config/columnDefinitions.js';
import { parseScarIndexNumeric } from '../../../utils/scarIndexSort.js';
import { generateFileName, getCaseStage, updateJurisdictionalAssignment, isCaseSettled, isMasterPetitionCase, getSettlementInfo } from './utils/helpers.js';
import { isBARCase, isSCARCase } from '../../../utils/caseFiltering.js';
import { getMostRecentOffer, getMostRecentOfferValue, isAventineOfferFromPriorRound, isTownOfferFromPriorRound } from '../../../utils/mostRecentOfferUtils.js';
import MunicipalResultsBanner from '../MunicipalResults/MunicipalResultsBanner.js';
import CustomToolbar from './components/CustomToolbar.js';
// import { handleUnifiedOfferAction } from './services/unifiedOfferService.js';
import { 
  updateOffersBatch
} from './services/realtimeOfferService.js';
import { updateOfferWithDialog } from './services/simpleOfferUpdateWithDialog.js';
import OfferRoundConfirmationDialog from './components/OfferRoundConfirmationDialog.js';
import { cancelOffers } from '../CancelOffers/cancelOfferService.js';
import VacateStipDialog from '../CancelOffers/VacateStipDialog.js';
import { deleteNegotiationEntry } from './services/deleteNegotiationService.js';
import { applyOfferRowPatch, buildOfferRowPatchFromCase, refreshMunicipalOfferCard } from './services/offerGridUpdateService.js';
import {
  createRows,
  getMaxNegotiationRounds,
  calculateProprietyWorkup,
  getStatusInfo,
  workupValueFromSubjectMarketValue,
} from './services/summaryRowCreationService.js';
import { getCaseStrengthPercentage, getCaseStrengthColor } from '../../../utils/caseStrengthCalculator.js';
import { getIFMVUtil } from '../../../utils/getAVUtil';
import SummaryCaseTablePollingComponent from './utils/SummaryCaseTablePollingComponent.js';
import WorkupViewerDialog from '../Workups/WorkupVisualization/WorkupViewerDialog.js';
import EQWarningDialog from './components/EQWarningDialog.js';
import { processSettlement } from '../Settlement/settlementService.js';
import { getDefaultSettlementStage } from '../Settlement/settlementStageUtils.js';
import { getConversionFactor } from '../../../utils/conversionUtils.js';
import {
  runRowCompletionAnimation,
  revertRowCompletionStyling,
} from './utils/rowCompletionAnimations.js';
import { parseCourtDateCalendarKey, parseCourtDateSortValue, UNASSIGNED_COURT_DATE, isUnassignedCourtDateCase } from '../../../common/dateUtils.js';
import TownViewTutorialOverlay from '../../TownView/tutorial/TownViewTutorialOverlay.js';

/** Identifies the toolbar-driven court date filter item so user-added items are never mistaken for it. */
const TOWN_COURT_DATE_FILTER_ID = 'town-court-date';

const courtDateFilterKeyFromItem = (item) => {
  if (!item) return null;
  if (item.operator === 'unassignedDate') return UNASSIGNED_COURT_DATE;
  const value = Array.isArray(item.value) ? item.value[0] : item.value;
  return value ?? null;
};

// Leaving the debounced updates in for negotiation obj in case we want to setup a state sync
// ie: store the value (temp table, or browser storage, temp table better?)
function SummaryCaseReviewTableRefactored({
  handleUpdateStateCallback,
  negotiationCases,
  onFilterChange,
  loading = false,
}) {
  const [showRoundColumns, setShowRoundColumns] = useState(false);
  const [offersChanged, setOffersChanged] = useState(false);
  // Read AV toggle directly from persisted store (no local state needed)
  const avToggleActive = usePersistedDataStore((state) => state.settlementIsAVMode);
  const setAvToggleActive = usePersistedDataStore((state) => state.setSettlementIsAVMode);

  const { townView, user } = useUser();
  const theme = useTheme();
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('xl'));

  // State management
  const [selectionModel, setSelectionModel] = React.useState(createEmptyRowSelection);
  const apiRef = useGridApiRef();
  const gridShellRef = useRef(null);
  const focusTownOfferCell = useCallback((row) => {
    if (!apiRef?.current || !row || row._isSettled || row.StipFinalized || row.AwaitingDecision) return;
    const id = row.id ?? row.parcel_id;
    if (id == null) return;
    apiRef.current.setCellFocus(id, 'TownOffer');
    requestAnimationFrame(() => {
      try {
        apiRef.current.startCellEditMode({ id, field: 'TownOffer' });
      } catch (error) {
        logger.log('Error starting TownOffer edit mode:', error?.message);
      }
    });
  }, [apiRef]);
  
  // Pagination state
  const [pageSize, setPageSize] = useState(500);
  const [page, setPage] = useState(0);
  
  // State for workup viewer dialog
  const [workupViewerOpen, setWorkupViewerOpen] = useState(false);
  const [workupViewerFiles, setWorkupViewerFiles] = useState([]);
  const [workupViewerNyscefFiles, setWorkupViewerNyscefFiles] = useState([]);
  const [workupViewerType, setWorkupViewerType] = useState('sales');
  const [workupViewerAddress, setWorkupViewerAddress] = useState('');
  const [workupViewerScarIndex, setWorkupViewerScarIndex] = useState('');
  
  // State for EQ warning dialog
  const [eqWarningDialogOpen, setEqWarningDialogOpen] = useState(false);
  // undefined = use link/run scope; null = user chose "All dates"; string = explicit court date
  const [townCourtDateFilterOverride, setTownCourtDateFilterOverride] = useState(undefined);
  const [townFilterModel, setTownFilterModel] = useState({ items: [] });
  
  // Sorting state - persisted in store
  const sortModel = usePersistedDataStore((state) => state.summaryCaseTableSortModel);
  const setSortModel = usePersistedDataStore((state) => state.setSummaryCaseTableSortModel);
  
  // Original parcel ID order - persisted in store to survive component remounts
  // This is derived from getNegotiationObj.cases index order
  const originalParcelIdsFromStore = usePersistedDataStore((state) => state.summaryCaseTableOriginalParcelIds);
  const setOriginalParcelIdsToStore = usePersistedDataStore((state) => state.setSummaryCaseTableOriginalParcelIds);
  
  
  // Ref to track offer values - contains both original values and user edits
  const offerValuesRef = useRef([]);
  
  
  // Ref to store user updates that override grid display (portfolio edits only)
  const userUpdatesRef = useRef(new Map());
  
  const setUserUpdate = useCallback((rowId, field, value) => {
    const currentUpdate = userUpdatesRef.current.get(rowId) || {};
    currentUpdate[field] = value;
    userUpdatesRef.current.set(rowId, currentUpdate);
  }, []);
  
  // Helper function to clear user update
  const clearUserUpdate = useCallback((rowId, field = null) => {
    if (field) {
      const currentUpdate = userUpdatesRef.current.get(rowId);
      if (currentUpdate) {
        delete currentUpdate[field];
        if (Object.keys(currentUpdate).length === 0) {
          userUpdatesRef.current.delete(rowId);
        } else {
          userUpdatesRef.current.set(rowId, currentUpdate);
        }
      }
    } else {
      userUpdatesRef.current.delete(rowId);
    }
  }, []);
  
  // Dialog states
  const [finalizing, setFinalizing] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  
  // Offer round confirmation dialog state
  const [offerRoundDialogOpen, setOfferRoundDialogOpen] = useState(false);
  const [offerRoundDialogData, setOfferRoundDialogData] = useState(null);

  // Simple dialog handlers
  const handleShowDialog = useCallback((dialogData) => {
    logger.log('handleShowDialog invoked with:', dialogData);
    // Wrap callbacks to close dialog after execution
    const dialogDataWithClose = {
      ...dialogData,
      onConfirm: () => {
        dialogData.onConfirm?.();
        handleDialogClose();
      },
      onCancel: () => {
        dialogData.onCancel?.();
        handleDialogClose();
      },
      onEditPriorRound: (roundNumber) => {
        dialogData.onEditPriorRound?.(roundNumber);
        handleDialogClose();
      }
    };
    setOfferRoundDialogData(dialogDataWithClose);
    setOfferRoundDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setOfferRoundDialogOpen(false);
    setOfferRoundDialogData(null);
  }, []);

  // Handle file view - open dialog instead of new tab
  const handleFileView = useCallback((file, workupType, allFiles, nyscefFiles = [], caseRow = null) => {
    const nyscef = Array.isArray(nyscefFiles) ? nyscefFiles : [];
    const hasNyscef = nyscef.some((f) => f?.Url);
    const files = Array.isArray(allFiles) ? allFiles : [];
    const hasWorkupUrl = files.some((f) => f?.Url || f?.url);
    if ((file && (file.Url || file.url)) || hasNyscef || hasWorkupUrl) {
      setWorkupViewerFiles(Array.isArray(allFiles) ? allFiles : []);
      setWorkupViewerNyscefFiles(nyscef);
      setWorkupViewerType(workupType);
      setWorkupViewerAddress(caseRow?.Address || '');
      setWorkupViewerScarIndex(caseRow?.SCARIndexNumber || '');
      setWorkupViewerOpen(true);
    }
  }, []);

  
  // Filter states - persisted in store. Town portal keeps its own stage so it
  // does not inherit the internal table's "all".
  const persistedStatusFilter = usePersistedDataStore((state) => state.summaryCaseTableStatusFilter);
  const setPersistedStatusFilter = usePersistedDataStore((state) => state.setSummaryCaseTableStatusFilter);
  const [townStatusFilter, setTownStatusFilter] = useState(null);
  const activeStage = usePersistedDataStore((state) => state.summaryCaseTableActiveStage);
  const setActiveStage = usePersistedDataStore((state) => state.setSummaryCaseTableActiveStage);

  // Town view: default stage to all. SCAR-only hides most rows because isSCARCase requires SCARFiled === 1.
  useEffect(() => {
    if (townView) {
      setActiveStage('all');
    }
  }, [townView, setActiveStage]);

  // URL params and store data
  const location = window.location;
  const queryParams = new URLSearchParams(location.search);
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const setNegotiationObj = usePersistedDataStore((state) => state.setNegotiationObj);
  const townStageDefault = (getNegotiationObj?.cases || []).some(isSCARCase) ? 'SCAR' : 'BAR';
  const statusFilter = townView ? (townStatusFilter ?? townStageDefault) : persistedStatusFilter;
  const setStatusFilter = townView ? setTownStatusFilter : setPersistedStatusFilter;
  const negotiationObjVersion = usePersistedDataStore((state) => state.negotiationObjVersion);
  const setNegotiationObjVersion = usePersistedDataStore((state) => state.setNegotiationObjVersion);
  const [townAcceptParcelId, setTownAcceptParcelId] = useState(null);
  const [townCancellingParcelId, setTownCancellingParcelId] = useState(null);

  const handleTownAcceptOffer = useCallback(async (row) => {
    if (!townView || !row || row._isSettled || row.StipFinalized || row.loading || row.AwaitingDecision) return;
    const ov = row.OfferValue;
    const av =
      ov != null && ov !== '' && ov !== 'None' && ov !== 'Loading...' && !Number.isNaN(Number(ov)) && Number(ov) > 0
        ? Math.round(Number(ov))
        : null;
    if (av == null) {
      toast.error('No offer to accept for this row.', { autoClose: false });
      return;
    }
    const neg = getNegotiationObj;
    if (!neg?.MuniCode || !neg?.TaxYear || !user?.userId) {
      toast.error('Missing municipality or user.', { autoClose: false });
      return;
    }
    const caseObject = neg.cases?.find((c) => c.parcel_id === row.parcel_id);
    if (!caseObject) {
      toast.error('Case not found.', { autoClose: false });
      return;
    }
    const context = {
      ...createOfferContext(user),
      townView: true,
    };
    setTownAcceptParcelId(row.parcel_id);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        runRowCompletionAnimation(gridShellRef.current, row.id);
      });
    });
    try {
      await new Promise((r) => setTimeout(r, 2000));
      await processSettlement(
        {
          ...caseObject,
          settlementValue: av,
          settlementDetails: {
            stage: getDefaultSettlementStage([caseObject], getNegotiationObj),
            isWithdrawal: false,
            withdrawalReason: '',
            conversionFactor: getConversionFactor(caseObject.EQ, caseObject.RAR),
          },
        },
        context
      );

      logger.log('Town view Accept settlement successful');
      const linkSession = linkSessionFromUser(user);
      if (linkSession && isFirstTownPortalResponseInSession(linkSession)) {
        let negotiatorId;
        try {
          negotiatorId = await negotiatorIdForSettlement(context);
        } catch (lookupErr) {
          logger.warn('Town portal notify: negotiator lookup failed', lookupErr);
        }
        maybeNotifyTownPortalFirstResponse({
          linkSession,
          action: 'accept_offer',
          parcelId: row.parcel_id,
          negotiatorId,
        });
      }
      // do not display toasts in the ctual prod town view
      // toast.success('Case settled successfully');
    } catch (error) {
      logger.error('Town view Accept settlement failed:', error);
      revertRowCompletionStyling(gridShellRef.current, row.id);
      if (error?.isAxiosError || error?.response) return;
      toast.error(
        townView
          ? 'Error settling case. Our team will look into this. If this is not resolved shortly, email info@aventine.ai'
          : error.message || 'Settlement failed',
        { autoClose: false },
      );
    } finally {
      setTownAcceptParcelId(null);
    }
  }, [townView, getNegotiationObj, user]);

  const handleTownCancelSettlement = useCallback(
    async (row) => {
      if (!townView || !row || row.loading || !row._isSettled) return;
      if (usePersistedDataStore.getState().stipsDownloadInFlight) return;

      setTownCancellingParcelId(row.parcel_id);
      revertRowCompletionStyling(gridShellRef.current, row.id ?? row.parcel_id);
      try {
        await cancelOffers({
          selectedRows: [row],
          getNegotiationObj,
          setNegotiationObj,
          setNegotiationObjVersion,
          user,
          townCancellation: true,
        });
      } catch (err) {
        logger.error('Town view cancel settlement failed:', err);
        if (err?.vacateDeclined || err?.isAxiosError || err?.response) return;
        toast.error(err.message || 'Could not cancel settlement. Please try again.');
      } finally {
        revertRowCompletionStyling(gridShellRef.current, row.id ?? row.parcel_id);
        setTownCancellingParcelId(null);
      }
    },
    [townView, user, getNegotiationObj, setNegotiationObj, setNegotiationObjVersion, gridShellRef],
  );

  const getCaseNotes = usePersistedDataStore((state) => state.caseNotes);
  const setFilterIndexes = usePersistedDataStore((state) => state.setFilterIndexes);
  const reportType = getNegotiationObj.reportType || 'standard';
  const taxYear = queryParams.get('TaxYear') || getNegotiationObj.TaxYear;
  const courtDate = queryParams.get('CourtDate') || 'All';
  const MuniCode = queryParams.get('MuniCode') || 'All';
  const fileName = generateFileName(MuniCode, taxYear, courtDate);
  const keys = useMemo(() => getKeysForView(townView, user), [townView, user]);
  const isAdmin = user?.userAccess?.level && ['admin', 'propadmin', 'OperationalAdmin'].includes(user.userAccess.level);
  const village = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('village') || 'All';
  }, []);
  const villageMode = village !== 'All';
  
  // Store data
  const getCompSheet = usePersistedDataStore((state) => state.compSheet, 
  // What is this equality function even for? It seems like its trying to memoize but idk why?
    (prev, next) => {
      if (prev.length !== next.length) return false;
      return prev[0]?.parcel_id?.[0] === next[0]?.parcel_id?.[0] &&
             prev[prev.length-1]?.parcel_id?.[0] === next[next.length-1]?.parcel_id?.[0];
    }
  );
  const getIsFetching = usePersistedDataStore((state) => state.isFetching);
  /** Persisted run-level RAR/EQ from getEvaluationDate — only for EQWarningDialog */
  const getRar = usePersistedDataStore((state) => state.rar);
  const getEq = usePersistedDataStore((state) => state.eq);
  
  // Initialize original parcel ID order from getNegotiationObj.cases when data is ready
  // Only set once when fetching is complete and we don't have it stored yet
  useEffect(() => {
    if (getNegotiationObj?.cases && Array.isArray(getNegotiationObj.cases) && 
        getNegotiationObj.cases.length > 0 && !getIsFetching && !originalParcelIdsFromStore) {
      const parcelIds = getNegotiationObj.cases.map(caseItem => caseItem.parcel_id).filter(Boolean);
      if (parcelIds.length > 0) {
        setOriginalParcelIdsToStore(parcelIds);
      }
    }
  }, [getNegotiationObj?.cases, getIsFetching, originalParcelIdsFromStore, setOriginalParcelIdsToStore]);

  // Show EQ warning dialog when fetching and EQ < RAR (persisted store values from getEvaluationDate)
  useEffect(() => {
    if (getIsFetching && getEq < getRar && getRar && getEq) {
      setEqWarningDialogOpen(true);
    }
  }, [getIsFetching, getRar, getEq]);


  // Get municipality lookup function
  const { getMunicipalityFromParcelId } = useNYData();

  // Helper function to find offer data by parcel ID
  const findOfferData = useCallback((parcelId) => {
    return offerValuesRef.current.find(offerData => offerData.ParcelID === parcelId);
  }, []);

  // ============================================================================
  // PURE DATA PIPELINE - NO MUTATIONS
  // ============================================================================
  // Layer 1: Master Cases (Source of Truth - Never Mutated by Filtering/Sorting)
  
  // This negotiationObjVersion is used to force updates for offers. this is inefficient and stupid.
  // these kinds of updates should be handled with the grid internals apiref edits.
  const masterCases = useMemo(() => {
    return getNegotiationObj?.cases || [];
  }, [getNegotiationObj?.cases]);

  const compAddressIndex = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(getCompSheet)) return map;
    getCompSheet.forEach((sheet) => {
      sheet?.parcel_id?.forEach((pid, i) => {
        const address = sheet.Address?.[i] || '';
        if (pid) map.set(pid, address);
        const tid = sheet.TransactionID?.[i];
        if (tid != null && tid !== '') {
          map.set(tid, address);
          map.set(String(tid), address);
        }
      });
    });
    return map;
  }, [getCompSheet]);

  // Layer 2: Enhanced Cases (Transform master cases with additional data)
  const enhancedCaseCache = useRef(new Map());
  const enhancedCaseCacheVersionRef = useRef(negotiationObjVersion);
  const enhancedCaseData = useMemo(() => {
    if (enhancedCaseCacheVersionRef.current !== negotiationObjVersion) {
      enhancedCaseCache.current.clear();
      enhancedCaseCacheVersionRef.current = negotiationObjVersion;
    }
    return masterCases.map((item, index) => {
      const compSheet =
        Array.isArray(getCompSheet) && getCompSheet.length > index
          ? getCompSheet[index]
          : null;

      const cached = enhancedCaseCache.current.get(item.parcel_id);
      if (cached && cached.caseRef === item && cached.compSheetRef === compSheet) {
        return cached.data;
      }

      // Extract comp IDs and addresses - left it as legacy but likely unnecessary now.
      let compIds = [];
      try {
        const compsObj = typeof item.Comps === 'string'
          ? JSON.parse(item.Comps)
          : item.Comps || {};
        compIds = Object.values(compsObj);
      } catch (e) { /* ignore parse errors */ }
      
      const compAddresses = compIds.map((pid) => compAddressIndex.get(pid) || '');
      
      // Calculate IFMV (needed for impliedReduction calculation)
      const ifmv = getIFMVUtil(item);
      
      // Calculate ProprietyWorkup early (town view: row SubjectMarketValue; else from comps)
      let proprietyWorkup = 0;
      if (townView) {
        proprietyWorkup = workupValueFromSubjectMarketValue(item, compSheet);
      } else if (compSheet) {
        try {
          const workupResult = calculateProprietyWorkup(item, compSheet);
          proprietyWorkup = workupResult === 'loading' ? 0 : (workupResult || 0);
        } catch (e) {
          logger.error('Error calculating ProprietyWorkup in enhancedCaseData:', e);
        }
      }
      
      // Calculate impliedReduction early
      let impliedReduction = 0;
      if (proprietyWorkup && ifmv) {
        impliedReduction = ((proprietyWorkup - ifmv) / ifmv) * 100;
      }
      
      // Determine Taxes with fallback: case Taxes first, then compSheet Taxes[0]
      const compSheetTaxes0 = compSheet && Array.isArray(compSheet.Taxes)
        ? compSheet.Taxes[0]
        : (compSheet && compSheet.Taxes !== undefined ? compSheet.Taxes : null);
      
      const resolvedTaxes =
        item.Taxes !== null &&
        item.Taxes !== undefined &&
        item.Taxes !== ''
          ? item.Taxes
          : compSheetTaxes0 ?? null;

      // Calculate CaseStrength early for sorting - use original case index
      // This will be recalculated in row creation based on current row position for display
      let caseStrength = null;
      let caseStrengthColor = null;
      if (compSheet) {
        try {
          // Find the index in the original getNegotiationObj.cases array
          const caseIndex = getNegotiationObj?.cases?.findIndex(
            (c) => c.parcel_id === item.parcel_id
          ) ?? -1;
          
          if (caseIndex >= 0) {
            const caseStrengthData = getCaseStrengthPercentage(caseIndex, reportType);
            caseStrength = caseStrengthData?.caseStrengthA ?? null;
            if (caseStrength != null) {
              caseStrengthColor = getCaseStrengthColor(caseStrength);
            }
          }
        } catch (e) {
          logger.error('Error calculating CaseStrength in enhancedCaseData:', e);
        }
      }

      // Calculate statusDisplay early
      let statusDisplay = 'Not Filed';
      try {
        const statusInfo = getStatusInfo(item, getNegotiationObj);
        statusDisplay = statusInfo?.statusDisplay || 'Not Filed';
      } catch (e) {
        logger.error('Error calculating statusDisplay in enhancedCaseData:', e);
      }

      let requestedAV = null;
      const mostRecentAventineOffer = getMostRecentOfferValue(item.Negotiations || [], 'Aventine');
      const offerValue = mostRecentAventineOffer !== null ? mostRecentAventineOffer : (item.OfferValue || null);
      
      if (offerValue !== null && offerValue !== undefined && offerValue > 0) {
        requestedAV = Math.round(offerValue);
      } else {
        // Check for settlement value for inactive cases
        const isActive = !isCaseSettled(item);
        if (!isActive) {
          const settlementInfo = getSettlementInfo(item);
          const settlementValue = settlementInfo.settlementValue || 0;
          if (settlementValue > 0) {
            requestedAV = Math.round(settlementValue);
          }
        }
      }

      let townReqAV = null;
      const mostRecentTownOffer = getMostRecentOfferValue(item.Negotiations || [], 'Town');
      const townOffer = mostRecentTownOffer !== null ? mostRecentTownOffer : (item.TownOffer || null);
      
      if (townOffer !== null && townOffer !== undefined && townOffer > 0) {
        townReqAV = Math.round(townOffer);
      }

      // Calculate OfferDiff early
      let offerDiff = null;
      if (townOffer !== null && townOffer !== undefined && townOffer > 0 && 
          offerValue !== null && offerValue !== undefined && offerValue > 0) {
        offerDiff = townOffer - offerValue;
      }

      const data = {
        ...item,
        Taxes: resolvedTaxes,
        ProprietyWorkup: proprietyWorkup,
        CaseStrength: caseStrength,
        CaseStrengthColor: caseStrengthColor,
        RequestedAV: requestedAV,
        TownReqAV: townReqAV,
        OfferDiff: offerDiff,
        statusDisplay: statusDisplay,
        impliedReduction: impliedReduction,
        CompsCount: compIds.length,
        Comp1: compAddresses[0] || '',
        Comp2: compAddresses[1] || '',
        Comp3: compAddresses[2] || '',
        Comp4: compAddresses[3] || '',
        Comp5: compAddresses[4] || '',
        CaseNotes: getCaseNotes?.[index] ?? '',
        RepID: item.RepID ?? '',
        Muni: getMunicipalityFromParcelId(item.parcel_id),
        EQ: item.EQ,
        RAR: item.RAR,
        ...(townView && {
          TownResponse: item.TownResponse ?? '',
          TownCounter: item.TownCounter ?? '',
          TownNotes: item.TownNotes ?? '',
        }),
      };

      enhancedCaseCache.current.set(item.parcel_id, {
        caseRef: item,
        compSheetRef: compSheet,
        data,
      });
      return data;
    });
  }, [masterCases, getCompSheet, compAddressIndex, getMunicipalityFromParcelId, getCaseNotes, townView, reportType, getNegotiationObj, negotiationObjVersion]);


  // Layer 3: Filtered Cases (Pure derivation - no mutation)
  const filteredCases = useMemo(() => {
    let filtered = enhancedCaseData;

    // Town portal uses the same stage pipe as the case table: BAR is only-BAR, SCAR is SCAR-filed, Art 7 is its own stage.
    if (townView) {
      filtered = filtered.filter((caseItem) => {
        if (statusFilter === 'Art7') return isMasterPetitionCase(caseItem);
        if (statusFilter === 'SCAR') return isSCARCase(caseItem);
        if (statusFilter === 'Settled') return isCaseSettled(caseItem) && (isBARCase(caseItem) || isSCARCase(caseItem));
        return isBARCase(caseItem);
      });
    } else if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter((caseItem) => {
        const isSettled = isCaseSettled(caseItem);
        const caseStatus = isSettled ? 'Settled' : 'Unsettled';
        return statusFilter === caseStatus;
      });
    }

    // Apply stage filter
    if (activeStage && activeStage !== 'all') {
      filtered = filtered.filter((caseItem) => {
        if (activeStage === 'BAR' && isBARCase(caseItem)) return true;
        if (activeStage === 'SCAR' && isSCARCase(caseItem)) return true;
        if (activeStage === 'Art7' && isMasterPetitionCase(caseItem)) return true;
        return false;
      });
    }

    return filtered;
  }, [enhancedCaseData, statusFilter, activeStage, townView]);

  // Helper function to extract text from statusDisplay (handles both strings and React elements)
  const getStatusTextForSorting = useCallback((statusDisplay) => {
    if (typeof statusDisplay === 'string') {
      return statusDisplay;
    }
    
    // If it's a React element, extract text from children recursively
    if (statusDisplay && typeof statusDisplay === 'object' && statusDisplay.props) {
      const children = statusDisplay.props.children;
      
      if (Array.isArray(children)) {
        // Handle arrays of children (like ["Awaiting", "Countersign"])
        return children
          .map(child => {
            if (typeof child === 'string') {
              return child;
            }
            if (child && typeof child === 'object') {
              // Recursively extract text from nested React elements
              return getStatusTextForSorting(child);
            }
            return '';
          })
          .filter(Boolean)
          .join(' ');
      } else if (typeof children === 'string') {
        return children;
      } else if (children && typeof children === 'object') {
        // Single child element, recurse into it
        return getStatusTextForSorting(children);
      }
    }
    
    return '';
  }, []);

  // Helper function to extract numeric value from a string that might have formatting (dollar signs, commas, percentages, etc.)
  const extractNumericValue = useCallback((value) => {
    if (value == null || value === '' || value === 'None') {
      return null;
    }
    
    // If it's already a number, return it
    if (typeof value === 'number') {
      return isNaN(value) ? null : value;
    }
    
    // If it's a string, extract the numeric part
    if (typeof value === 'string') {
      // Remove dollar signs, commas, percentage signs, and other non-numeric characters except minus and decimal point
      const cleaned = value.replace(/[^-\d.]/g, '');
      const numValue = parseFloat(cleaned);
      return isNaN(numValue) ? null : numValue;
    }
    
    return null;
  }, []);

  // Helper function to get sort value from a case (similar to valueGetter but works on cases)
  const getSortValue = useCallback((caseItem, field) => {
    
    // Helper to check if value is blank
    const isBlank = (val) => {
      return val == null || val === '' || val === 'None' || val === 'none' || 
             (typeof val === 'number' && (isNaN(val) || val <= 0)) ||
             (typeof val === 'string' && val.trim() === '');
    };

    // Handle ProprietyWorkup - extract numeric value from the field
    if (field === 'ProprietyWorkup') {
      const value = extractNumericValue(caseItem.ProprietyWorkup);
      return value != null && value > 0 ? value : null;
    }

    // Handle CaseStrength - extract numeric value (0 is valid, so don't use isBlank)
    if (field === 'CaseStrength') {
      if (caseItem.CaseStrength == null || caseItem.CaseStrength === '' || caseItem.CaseStrength === 'None') {
        return null;
      }
      const value = extractNumericValue(caseItem.CaseStrength);
      return value != null ? value : null;
    }

    // Handle RequestedAV - extract numeric value only (ignore percentage display)
    if (field === 'RequestedAV') {
      const value = extractNumericValue(caseItem.RequestedAV);
      return value != null && value > 0 ? value : null;
    }

    // Handle OfferDiff - extract numeric value (0 and negative values are valid)
    if (field === 'OfferDiff') {
      if (caseItem.OfferDiff == null || caseItem.OfferDiff === '' || caseItem.OfferDiff === 'None') {
        return null;
      }
      const value = extractNumericValue(caseItem.OfferDiff);
      return value != null ? value : null;
    }

    // Handle status - extract text from statusDisplay for alphabetical sorting
    if (field === 'status') {
      const statusDisplay = caseItem.statusDisplay;
      if (statusDisplay) {
        const statusText = getStatusTextForSorting(statusDisplay);
        return statusText || '';
      }
      return '';
    }

    // Handle numeric fields that need special valueGetter logic
    if (field === 'OfferValue') {
      if (!isBlank(caseItem.OfferValue)) {
        const numValue = Number(caseItem.OfferValue);
        return (!isNaN(numValue) && numValue > 0) ? numValue : null;
      }
      const offerValue = getMostRecentOfferValue(caseItem.Negotiations, 'Aventine');
      return (offerValue !== null && offerValue > 0) ? Number(offerValue) : null;
    }
    if (field === 'TownOffer') {
      if (!isBlank(caseItem.TownOffer)) {
        const numValue = Number(caseItem.TownOffer);
        return (!isNaN(numValue) && numValue > 0) ? numValue : null;
      }
      const offerValue = getMostRecentOfferValue(caseItem.Negotiations, 'Town');
      return (offerValue !== null && offerValue > 0) ? Number(offerValue) : null;
    }
    
    // Handle TownReqAV - extract numeric value only (ignore percentage display)
    if (field === 'TownReqAV') {
      const value = extractNumericValue(caseItem.TownReqAV);
      return value != null && value > 0 ? value : null;
    }

    // Handle IFMV - use unified getter to support IFMVTown/IFMVVillage fields
    if (field === 'IFMV') {
      const value = extractNumericValue(getIFMVUtil(caseItem));
      return value != null && value > 0 ? value : null;
    }

    if (field === 'SCARHearingDate') {
      return parseCourtDateSortValue(caseItem.SCARHearingDate);
    }

    if (field === 'SCARIndexNumber') {
      return parseScarIndexNumeric(caseItem.SCARIndexNumber);
    }

    if (field === 'SDName') {
      const v = caseItem.SDName;
      const raw = Array.isArray(v) ? v[0] : v;
      const s = String(raw ?? '').trim();
      return s && s.toLowerCase() !== 'none' ? s : null;
    }

    if (field === 'CompsCount') {
      if (typeof caseItem.CompsCount === 'number' && !isNaN(caseItem.CompsCount)) {
        return caseItem.CompsCount;
      }
      try {
        const compsObj = typeof caseItem.Comps === 'string'
          ? JSON.parse(caseItem.Comps)
          : caseItem.Comps || {};
        return compsObj && typeof compsObj === 'object' ? Object.values(compsObj).length : 0;
      } catch (e) {
        return 0;
      }
    }

    // For other fields, use the field value directly
    const value = caseItem[field];
    if (isBlank(value)) return null;
    return value;
  }, [extractNumericValue, getStatusTextForSorting]);

  // Layer 4: Sorted Cases (Pure derivation - no mutation)
  const sortedCases = useMemo(() => {
    if (!sortModel || sortModel.length === 0) {
      return filteredCases;
    }

    // Create a copy to sort (don't mutate original)
    const sorted = [...filteredCases].sort((a, b) => {
      for (const sortItem of sortModel) {
        const { field, sort } = sortItem;
        
        // Get sort values using helper function
        const aValue = getSortValue(a, field);
        const bValue = getSortValue(b, field);
        
        // Handle null/undefined values - nulls should always go to the end when sorting ascending
        if (aValue == null && bValue == null) {
          continue;
        }
        if (aValue == null) return sort === 'asc' ? 1 : -1;
        if (bValue == null) return sort === 'asc' ? -1 : 1;
        
        // Compare values
        let comparison = 0;
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }
        
        if (comparison !== 0) {
          return sort === 'asc' ? comparison : -comparison;
        }
      }
      return 0;
    });

    return sorted;
  }, [filteredCases, sortModel, getSortValue]);

  // Layer 5: Rows with User Overrides (Pure derivation - no mutation)
  const memoizedCreateRows = useCallback(
    (cases, fullNegotiationCases) => {
      return createRows(cases, fullNegotiationCases, {
        townView,
        village,
        getCompSheet,
        getNegotiationObj,
        getMunicipalityFromParcelId,
        reportType,
        getCaseNotes,
      });
    },
    [townView, village, getCompSheet, getNegotiationObj, getMunicipalityFromParcelId, reportType, getCaseNotes, negotiationObjVersion]
  );

  // Simple loading state - check if we have uids but no cases yet
  const uids = getNegotiationObj?.uids || [];
  const isLoading = uids.length > 0 && !masterCases?.length;

  const userUpdatesCacheVersionRef = useRef(negotiationObjVersion);

  const rows = useMemo(() => {
    const negotiationVersionBumped = userUpdatesCacheVersionRef.current !== negotiationObjVersion;
    if (negotiationVersionBumped) {
      userUpdatesRef.current.clear();
      userUpdatesCacheVersionRef.current = negotiationObjVersion;
    }

    // If loading, create loading rows from uids
    if (isLoading && uids.length > 0) {
      return uids.map((parcelId, index) => ({
        id: parcelId,
        Comp: index + 1,
        loading: true,
        Address: parcelId || '',
        parcel_id: parcelId || '',
      }));
    }
    
    if (!sortedCases.length) return [];
    
    // Create rows from sorted cases
    const baseRows = memoizedCreateRows(sortedCases, enhancedCaseData);
    
    // Apply user overrides
    return baseRows.map(row => {
      const rowId = row.id || row.parcel_id;
      const userUpdate = userUpdatesRef.current.get(rowId);
      
      if (userUpdate) {
        return { ...row, ...userUpdate };
      }
      
      return row;
    });
  }, [sortedCases, enhancedCaseData, memoizedCreateRows, isLoading, uids, negotiationObjVersion]);

  const duplicateRowIds = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    rows.forEach((row) => row && (seen.has(row.id) ? dupes.add(row.id) : seen.add(row.id)));
    return [...dupes];
  }, [rows]);
  const dataGridRows = useMemo(() => (duplicateRowIds.length ? [] : rows.filter(Boolean)), [rows, duplicateRowIds]);
  useEffect(() => {
    if (duplicateRowIds.length) logger.error('Summary table received duplicate row ids:', duplicateRowIds);
  }, [duplicateRowIds]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const scopedTownCourtDateKey = useMemo(() => {
    if (!townView) return null;
    return parseCourtDateCalendarKey(resolveCourtDateForContext(getNegotiationObj, user));
  }, [townView, getNegotiationObj?.CourtDate, user?.court_date]);

  const townCourtDateFilterKey =
    townCourtDateFilterOverride !== undefined
      ? townCourtDateFilterOverride
      : scopedTownCourtDateKey;

  const townCourtDateKeys = useMemo(() => {
    if (!townView) return [];
    const uniq = new Set();
    for (const row of rows) {
      if (row.loading) continue;
      const k = parseCourtDateCalendarKey(row.SCARHearingDate);
      if (k) uniq.add(k);
    }
    return Array.from(uniq).sort();
  }, [townView, rows]);

  const hasUnassignedCourtDate = useMemo(
    () => townView && rows.some((row) => !row.loading && isUnassignedCourtDateCase(row)),
    [townView, rows],
  );

  const townInitialLoadComplete = !loading && !isLoading;

  const effectiveTownCourtDateFilterKey = useMemo(() => {
    if (!townView || !townCourtDateFilterKey || !townInitialLoadComplete) return null;
    if (townCourtDateFilterKey === UNASSIGNED_COURT_DATE) {
      return hasUnassignedCourtDate ? UNASSIGNED_COURT_DATE : null;
    }
    if (townCourtDateKeys.length === 0 || townCourtDateKeys.includes(townCourtDateFilterKey)) {
      return townCourtDateFilterKey;
    }
    return null;
  }, [townView, townCourtDateFilterKey, townCourtDateKeys, townInitialLoadComplete, hasUnassignedCourtDate]);

  // The court-date item is seeded from the toolbar selector but stays user-editable; everything
  // else in the model belongs to the filter panel and must round-trip untouched.
  useEffect(() => {
    if (!townView) return;
    setTownFilterModel((prev) => {
      const items = prev.items ?? [];
      const existing = items.find((i) => i.id === TOWN_COURT_DATE_FILTER_ID);
      const existingKey = courtDateFilterKeyFromItem(existing);
      if ((existingKey ?? null) === (effectiveTownCourtDateFilterKey ?? null)) return prev;
      const rest = items.filter((i) => i.id !== TOWN_COURT_DATE_FILTER_ID);
      const courtDateItem = !effectiveTownCourtDateFilterKey
        ? null
        : effectiveTownCourtDateFilterKey === UNASSIGNED_COURT_DATE
          ? { id: TOWN_COURT_DATE_FILTER_ID, field: 'SCARHearingDate', operator: 'unassignedDate' }
          : { id: TOWN_COURT_DATE_FILTER_ID, field: 'SCARHearingDate', operator: 'isAnyOf', value: [effectiveTownCourtDateFilterKey] };
      return {
        ...prev,
        items: courtDateItem ? [courtDateItem, ...rest] : rest,
      };
    });
  }, [townView, effectiveTownCourtDateFilterKey]);

  const handleTownCourtDateFilterModelChange = useCallback((model) => {
    if (!townView) return;
    setTownFilterModel(model);
    const item = model.items.find((i) => i.id === TOWN_COURT_DATE_FILTER_ID);
    const value = courtDateFilterKeyFromItem(item);
    if (value) {
      setTownCourtDateFilterOverride(String(value));
    } else if (effectiveTownCourtDateFilterKey) {
      // Only an active court date item that the user removed means "All dates"; a model change
      // while none is seeded (quick filter, other columns) must not drop the run scope.
      setTownCourtDateFilterOverride(null);
    }
  }, [townView, effectiveTownCourtDateFilterKey]);

  const showInitialLoadingOverlay = loading && dataGridRows.length === 0;

  // Compute filtered/sorted indices for CompNavigation
  // Map sortedCases back to their indices in masterCases
  // Hate this flow but working for now.
  useEffect(() => {
    if (!sortedCases.length || !masterCases.length) {
      // If no sorted cases or no master cases, clear filter indexes
      setFilterIndexes(undefined);
      return;
    }

    // Create a map of parcel_id to index in masterCases for fast lookup
    const parcelIdToIndex = new Map();
    masterCases.forEach((caseItem, index) => {
      if (caseItem.parcel_id) {
        parcelIdToIndex.set(caseItem.parcel_id, index);
      }
    });

    // Map sortedCases to their indices in masterCases
    const indices = sortedCases
      .map(caseItem => {
        if (caseItem.parcel_id) {
          return parcelIdToIndex.get(caseItem.parcel_id);
        }
        return null;
      })
      .filter(index => index !== null && index !== undefined);

    // Only update if indices have changed
    if (indices.length > 0) {
      setFilterIndexes(indices);
    } else {
      setFilterIndexes(undefined);
    }
  }, [sortedCases, masterCases, setFilterIndexes]);

  // Simple undo/redo state management
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isBrowserNavigation, setIsBrowserNavigation] = useState(false);
  const [historyStateCount, setHistoryStateCount] = useState(0);
  
  // const canUndo = undoStack.length > 0;
  // const canRedo = redoStack.length > 0;

  // Get context for real-time updates using centralized utility
  const getUpdateContext = useCallback(() => ({
    ...createOfferContext(user),
    ...(townView ? { townView: true } : {}),
    ...(townView && user?.isLinkAuthenticated
      ? { linkTownSession: linkSessionFromUser(user) }
      : {}),
  }), [user, townView]);
  
  // Track changes for undo/redo
  const trackChange = useCallback((parcelId, field, oldValue, newValue) => {
    const change = {
      parcelId,
      field,
      oldValue,
      newValue,
      timestamp: Date.now()
    };
    
    setUndoStack(prev => [...prev, change]);
    setRedoStack([]); // Clear redo stack when new change is made
    
    // Push state to browser history for back/forward button support
    const newHistoryStateCount = historyStateCount + 1;
    setHistoryStateCount(newHistoryStateCount);
    
    const historyState = {
      type: 'table-change',
      change,
      historyStateCount: newHistoryStateCount
    };
    
    window.history.pushState(historyState, '', window.location.href);
  }, [undoStack.length, historyStateCount]);
  
  // Undo function
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) {
      return;
    }
    
    const lastChange = undoStack[undoStack.length - 1];
    const { parcelId, field, oldValue } = lastChange;
    
    // Update the negotiation object directly
    const updatedNegotiationObj = { ...getNegotiationObj };
    const caseIndex = updatedNegotiationObj.cases.findIndex(
      caseItem => caseItem.parcel_id === parcelId
    );
    
        if (caseIndex !== -1) {
          updatedNegotiationObj.cases[caseIndex] = {
            ...updatedNegotiationObj.cases[caseIndex],
            [field]: oldValue
          };
          
          setNegotiationObj(updatedNegotiationObj);
          setNegotiationObjVersion(prev => prev + 1);
          
          // Trigger real-time database update for the undone change
          const updateData = { [field]: oldValue };
          if (field === 'TownOffer') {
            updateData.OfferedBy = 'Town';
          }
          
          // Convert to offer format for updateOffersBatch
          const offer = {
            parcelId,
            offerValue: updateData[field],
            offeredBy: updateData.OfferedBy || (field === 'TownOffer' ? 'Town' : 'Aventine'),
            offerDate: new Date().toISOString(),
            comments: updateData.Comments,
            ...updateData
          };
          
          updateOffersBatch([offer], getUpdateContext(), { updateFrontend: true })
            .then((result) => {
              // Success callback - no logging needed
            })
            .catch((error) => {
              toast.error(`Failed to sync undo to database: ${error.error}`);
            }
          );
        }
    
    // Move change from undo to redo stack
    setUndoStack(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, lastChange]);
    
    // Only show toast if not triggered by browser navigation
    if (!isBrowserNavigation) {
      toast.info('Changes undone');
    }
    
    // Reset browser navigation flag
    setIsBrowserNavigation(false);
  }, [undoStack, getNegotiationObj, setNegotiationObj, setNegotiationObjVersion, getUpdateContext, isBrowserNavigation]);
  
  // Redo function
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    
    const changeToRedo = redoStack[redoStack.length - 1];
    const { parcelId, field, newValue } = changeToRedo;
    
    // Update the negotiation object directly
    const updatedNegotiationObj = { ...getNegotiationObj };
    const caseIndex = updatedNegotiationObj.cases.findIndex(
      caseItem => caseItem.parcel_id === parcelId
    );
    
        if (caseIndex !== -1) {
          updatedNegotiationObj.cases[caseIndex] = {
            ...updatedNegotiationObj.cases[caseIndex],
            [field]: newValue
          };
          
          setNegotiationObj(updatedNegotiationObj);
          setNegotiationObjVersion(prev => prev + 1);
          
          // Trigger real-time database update for the redone change
          const updateData = { [field]: newValue };
          if (field === 'TownOffer') {
            updateData.OfferedBy = 'Town';
          }
          
          // Convert to offer format for updateOffersBatch
          const offer = {
            parcelId,
            offerValue: updateData[field],
            offeredBy: updateData.OfferedBy || (field === 'TownOffer' ? 'Town' : 'Aventine'),
            offerDate: new Date().toISOString(),
            comments: updateData.Comments,
            ...updateData
          };
          
          updateOffersBatch([offer], getUpdateContext(), { updateFrontend: true })
            .then((result) => {
              // Success callback - no logging needed
            })
            .catch((error) => {
              toast.error(`Failed to sync redo to database: ${error.error}`);
            }
          );
        }
    
    // Move change from redo to undo stack
    setRedoStack(prev => prev.slice(0, -1));
    setUndoStack(prev => [...prev, changeToRedo]);
    
    // Only show toast if not triggered by browser navigation
    if (!isBrowserNavigation) {
      toast.info('Changes redone');
    }
    
    // Reset browser navigation flag
    setIsBrowserNavigation(false);
  }, [redoStack, getNegotiationObj, setNegotiationObj, setNegotiationObjVersion, getUpdateContext, isBrowserNavigation]);

  // Initialize offer values for change tracking -- syncs the ref with the new values
  useEffect(() => {
    if (rows.length > 0) {
      offerValuesRef.current = rows.map(row => {
        const parcelId = row.parcel_id;
        
        // Store original values and initialize current values
        return {
          ParcelID: parcelId,
          original: {
            OfferValue: row.OfferValue,
            PortfolioOffer: row.PortfolioOffer,
            PortfolioRequestedAV: row.PortfolioRequestedAV,
            TownNotes: row.TownNotes
          },
          current: {
            OfferValue: row.OfferValue,
            PortfolioOffer: row.PortfolioOffer,
            PortfolioRequestedAV: row.PortfolioRequestedAV,
            TownNotes: row.TownNotes
          }
        };
      });
    }
  }, [rows]);

  // Precompute row numbers (1-based) by id
  const rowNumberById = useMemo(() => {
    const map = new Map();
    rows.forEach((r, idx) => map.set(r.id, idx + 1));
    return map;
  }, [rows]);

  // Derive selected rows with attached row number
  const selectedRows = useMemo(() => {
    const selectedRowIds = getSelectedRowIds(selectionModel);
    return rows
      .filter(row => selectedRowIds.includes(row.id))
      .map(row => ({ ...row, _rowNumber: rowNumberById.get(row.id) }));
  }, [rows, selectionModel, rowNumberById]);


  

  // Update selection model when selection changes - selectedRows is derived automatically
  const handleRowSelectionModelChange = useCallback((newSelectionModel) => {
    // When checkboxSelectionVisibleOnly is true, the Data Grid manages selection per page
    // and passes the complete selection model (all selected rows across all pages).
    // We should trust what the Data Grid passes and just validate/filter it.

    const validRowIds = new Set(rows.map(row => row.id));
    const uniqueSelection = Array.from(new Set(getSelectedRowIds(newSelectionModel))).filter(id => validRowIds.has(id));

    setSelectionModel(createRowSelectionFromIds(uniqueSelection));
  }, [rows]);

  const handleScrollToRowId = useCallback((targetId) => {
    if (!apiRef?.current || !targetId) return;
    const rowIndex = rows.findIndex((r) => r.id === targetId);
    if (rowIndex < 0) return;
    // Defer to next frame so selection/DOM have settled
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => {
        const api = apiRef.current;
        if (!api) return;
        try {
          api.scrollToIndexes({ rowIndex });
        } catch {
          // ignore scroll errors
        }
      });
    } else {
      try {
        apiRef.current.scrollToIndexes({ rowIndex });
      } catch {
        // ignore scroll errors
      }
    }
  }, [rows, apiRef]);



  // Check if any rows are still loading based on actual row data
  // For town view, we don't have streaming updates, so always return false
  const hasLoadingRows = useMemo(() => {
    if (townView) return false;
    return rows.some(row => row.loading);
  }, [rows, townView]);

  // Clear user updates when negotiation object changes externally
  useEffect(() => {
    userUpdatesRef.current.clear();
  }, [negotiationObjVersion]);

  // Cleanup effect: clear refs on unmount
  useEffect(() => {
    return () => {
      // Clear offer values ref on unmount
      offerValuesRef.current = [];
      setOffersChanged(false);
      
      // Clear user updates ref on unmount
      userUpdatesRef.current.clear();
      
      // Clear undo/redo stacks on unmount
      setUndoStack([]);
      setRedoStack([]);
    };
  }, []);
  

  
  // Presence flags / visibility must not change identity when only row order changes (sort),
  // or DataGrid treats columns as new and resets widths.
  const columnFlagsRef = useRef(null);
  const columnFlags = useMemo(() => {
    const next = getColumnPresenceFlags(rows);
    const prev = columnFlagsRef.current;
    if (
      prev &&
      prev.hasNegotiatingCases === next.hasNegotiatingCases &&
      prev.hasPortfolioOffers === next.hasPortfolioOffers &&
      prev.hasCaseNotes === next.hasCaseNotes
    ) {
      return prev;
    }
    columnFlagsRef.current = next;
    return next;
  }, [rows]);

  const computedVisibilityRef = useRef(null);
  const dynamicColumnVisibility = useMemo(() => {
    const next = getDynamicColumnVisibility(rows, avToggleActive, townView, isCompactScreen);
    const prev = computedVisibilityRef.current;
    if (prev) {
      const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
      let same = true;
      for (const key of keys) {
        if (prev[key] !== next[key]) {
          same = false;
          break;
        }
      }
      if (same) return prev;
    }
    computedVisibilityRef.current = next;
    return next;
  }, [rows, avToggleActive, townView, isCompactScreen]);

  const {
    columnVisibilityModel,
    onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
    columnWidths,
    columnLayout,
  } = useColumnLayout(
    townView ? 'townSummary' : 'summary',
    keys,
    townView ? TOWN_COLUMN_VISIBILITY : INTERNAL_COLUMN_VISIBILITY,
    dynamicColumnVisibility,
  );


  // Cell className function for styling. This logic has gotten far too convoluted and dumb.
  const getCellClassName = useCallback((params) => {
    if (params.field === 'OfferValue') {
      const isInactive = !params.row.isActive;
      const notFiledAtScar = params.row.notFiledAtScar;

      if (townView || params.row.loading || notFiledAtScar || isInactive) {
        return '';
      }
    
      const rowOfferValue = params.row.OfferValue;
      const hasOfferValue =
        rowOfferValue !== null &&
        rowOfferValue !== 'None' &&
        rowOfferValue !== '' &&
        !isNaN(Number(rowOfferValue)) &&
        Number(rowOfferValue) > 0;

      const negotiations = params.row.Negotiations;
      if (negotiations && hasOfferValue && isAventineOfferFromPriorRound(negotiations)) {
        return 'outdated-offer-cell';
      }

      if (!hasOfferValue) {
        return 'missing-offer-cell';
      }

      const isOfferSentStatus = Boolean(params.row._aventineLatestOfferSent);
      if (isOfferSentStatus) {
        return 'finalized-offer-cell';
      }
      return 'pending-offer-cell';
    }
    
    if (params.field === 'TownOffer') {
      if (!params.row.isActive || params.row._isSettled || params.row.StipFinalized || params.row.AwaitingDecision) return '';
      const negotiations = params.row.Negotiations;
      if (!townView && negotiations && isTownOfferFromPriorRound(negotiations, false)) {
        return 'outdated-offer-cell';
      } else if (townView && negotiations && isTownOfferFromPriorRound(negotiations, true)) {
        return 'outdated-offer-cell';
      }
      return 'town-offer-cell';
    }
    
    return '';
  }, [townView]);
  
  // Calculate maximum number of negotiation rounds
  const maxRounds = useMemo(() => {
    const rounds = getMaxNegotiationRounds(rows);
    return rounds;
  }, [rows]);

  useEffect(() => {
    if (maxRounds <= 1) setShowRoundColumns(false);
  }, [maxRounds]);

  // THIS REFERENCE HAS GOTTEN SO UNWEIDLY. Needs a refactor to remove all the unnecessary crap in here. Likely bottlenecks performance causing unnecessary grid re-creations.
  // Column definitions using the extracted config — do NOT depend on `rows` identity (sort reorders rows).
  const columns = useMemo(() => {
    return createColumnDefinitions({
      townView,
      gridKeys: keys,
      focusTownOfferCell,
      onTownAcceptOffer: handleTownAcceptOffer,
      townAcceptParcelId,
      onTownCancelSettlement: townView ? handleTownCancelSettlement : null,
      hasNegotiatingCases: columnFlags.hasNegotiatingCases,
      hasPortfolioOffers: columnFlags.hasPortfolioOffers,
      hasCaseNotes: columnFlags.hasCaseNotes,
      avToggleActive, // Add AV toggle state
      maxRounds,
      isLoading,
      onCancelOffer: (parcelId) => {
        const singleRow = rowsRef.current.find(row => row.parcel_id === parcelId);
        if (!singleRow) {
          toast.error(`Row not found for parcel ID: ${parcelId}`);
          return;
        }
        
        cancelOffers({
          selectedRows: [singleRow],
          getNegotiationObj,
          setNegotiationObj,
          setNegotiationObjVersion
        }).then(() => {
          offerValuesRef.current = offerValuesRef.current.filter(offerData => offerData.ParcelID !== parcelId);
          const caseData = usePersistedDataStore.getState().negotiationObj?.cases?.find(
            (c) => c.parcel_id === parcelId,
          );
          applyOfferRowPatch(apiRef, parcelId, buildOfferRowPatchFromCase(singleRow, caseData));
          toast.success(`Successfully cancelled the offer for ${singleRow.Address || parcelId}.`);
        }).catch((error) => {
          logger.error('Error canceling single offer:', error);
          if (error?.vacateDeclined || error?.isAxiosError || error?.response) return;
          toast.error(error.message || 'An error occurred while canceling the offer. Please try again.');
        });
      },
      onDeleteOffer: (offerData) => {
        const { offerId, parcelId, offeredBy } = offerData;
        const gridRow = rowsRef.current.find((row) => row.parcel_id === parcelId);
        
        try {
          deleteNegotiationEntry({
            offerId,
            parcelId,
            taxYear: getNegotiationObj.TaxYear
          });
          const caseData = usePersistedDataStore.getState().negotiationObj?.cases?.find(
            (c) => c.parcel_id === parcelId,
          );
          if (gridRow) {
            applyOfferRowPatch(apiRef, parcelId, buildOfferRowPatchFromCase(gridRow, caseData));
          }
          refreshMunicipalOfferCard(offeredBy === 'Town' ? 'Town' : 'Aventine');
        } catch (error) {
          logger.error('Error deleting negotiation entry:', error);
          toast.error(error.message || 'An error occurred while deleting the offer. Please try again.');
        }
      },
      showRoundColumns,
      maxRounds,
      hasUnassignedCourtDate,
      onShowDialog: townView ? null : handleShowDialog,
      setOffersChanged,
      reportType,
      onFileView: handleFileView,
      getRows: () => rowsRef.current,
    }).map((col) => ({ ...col, ...columnWidths?.[col.field] }));
  }, [
    columnWidths,
    townView,
    keys,
    getNegotiationObj,
    columnFlags,
    setNegotiationObj,
    setNegotiationObjVersion,
    showRoundColumns,
    maxRounds,
    hasUnassignedCourtDate,
    avToggleActive,
    isLoading,
    handleShowDialog,
    setOffersChanged,
    reportType,
    handleFileView,
    focusTownOfferCell,
    handleTownAcceptOffer,
    townAcceptParcelId,
    handleTownCancelSettlement,
    apiRef,
  ]);
  

  // Handle sort model change - ONLY updates state, no mutations
  const handleSortModelChange = useCallback((newSortModel) => {
    // Update the sort model state in store
    // The pure data pipeline will automatically recompute sortedCases based on this change
    setSortModel(newSortModel);
  }, [setSortModel]);

  // Handle filter changes - ONLY updates state, no mutations
  const handleFilterChange = useCallback((newStatusFilter, newActiveStage) => {
    // Update filter state
    // The pure data pipeline will automatically recompute filteredCases based on these changes
    if (newStatusFilter !== undefined) setStatusFilter(newStatusFilter);
    if (newActiveStage !== undefined) setActiveStage(newActiveStage);
    
    const finalStatusFilter = newStatusFilter !== undefined ? newStatusFilter : statusFilter;
    const finalActiveStage = newActiveStage !== undefined ? newActiveStage : activeStage;
    
    // Notify parent
    if (onFilterChange) {
      onFilterChange({ activeStage: finalActiveStage, statusFilter: finalStatusFilter });
    }
  }, [statusFilter, activeStage, onFilterChange, setStatusFilter, setActiveStage]);

  // Handle cancel offers
  const handleCancelOffers = useCallback(async () => {
    try {
      const result = await cancelOffers({
        selectedRows,
        getNegotiationObj,
        setNegotiationObj,
        setNegotiationObjVersion
      });

      // Clear offer values ref after successful cancellation
      offerValuesRef.current = [];
      setOffersChanged(false);
      
      toast.success(`Successfully cancelled ${result.cancelledCount} offers.`);
    } catch (error) {
      logger.error('Error canceling offers:', error);
      if (error?.vacateDeclined || error?.isAxiosError || error?.response) return;
      toast.error(error.message || 'An error occurred while canceling offers. Please try again.');
    }
  }, [selectedRows, getNegotiationObj, setNegotiationObj, setNegotiationObjVersion]);


  
  // Assignment click handler
  const handleAssignmentClick = useCallback(() => {
    setAssignmentDialogOpen(true);
  }, []);
  
  // Generalized helper function to update negotiation object
  const updateNegotiationCase = useCallback((parcelId, updates) => {
    const caseIndex = getNegotiationObj.cases.findIndex(
      (caseItem) => caseItem.parcel_id === parcelId
    );
    
    if (caseIndex !== -1) {
      const currentCase = getNegotiationObj.cases[caseIndex];
      
      // Check if any values actually changed
      const hasChanges = Object.keys(updates).some(key => currentCase[key] !== updates[key]);
      
      if (hasChanges) {
        let updatedCase = { ...currentCase, ...updates };
        
        // Note: Negotiation updates are now handled by the realtime service
        // This function only handles basic case field updates
        
        const updatedNegotiationObj = { ...getNegotiationObj };
        updatedNegotiationObj.cases[caseIndex] = updatedCase;
        setNegotiationObj(updatedNegotiationObj);
      }
    }
  }, [getNegotiationObj, setNegotiationObj]);

  // Process row update function for DataGrid
  const processRowUpdate = useCallback((newRow, oldRow) => {
    const updatedRow = { ...newRow };
    logger.log('newRow', newRow);
    
    // Track changes for undo/redo functionality
    const hasOfferFieldChanges = 
      newRow.OfferValue !== oldRow.OfferValue ||
      newRow.TownOffer !== oldRow.TownOffer ||
      newRow.PortfolioOffer !== oldRow.PortfolioOffer ||
      newRow.PortfolioRequestedAV !== oldRow.PortfolioRequestedAV ||
      newRow.TownNotes !== oldRow.TownNotes;

      // Store user updates for immediate visual feedback
      if (hasOfferFieldChanges) {
        const rowId = newRow.id || newRow.parcel_id;
        if (newRow.PortfolioOffer !== oldRow.PortfolioOffer) {
          setUserUpdate(rowId, 'PortfolioOffer', newRow.PortfolioOffer);
        }
      }



    // If PortfolioOffer changed, update the store and trigger real-time update
    if (newRow.PortfolioOffer !== oldRow.PortfolioOffer) {
      // Handle null, undefined, empty string, 'None', or 0 values as null
      const newPortfolioOffer = (newRow.PortfolioOffer === null || newRow.PortfolioOffer === undefined || 
                               newRow.PortfolioOffer === '' || newRow.PortfolioOffer === 'None' || 
                               newRow.PortfolioOffer === 0) ? null : Number(newRow.PortfolioOffer);
      
      // Get the case data to extract complete offer information
      const portfolioCaseIndex = getNegotiationObj.cases.findIndex(
        (caseItem) => caseItem.parcel_id === newRow.parcel_id
      );
      
      if (portfolioCaseIndex !== -1) {
        const currentCase = getNegotiationObj.cases[portfolioCaseIndex];
        
        const result = getMostRecentOffer(currentCase.Negotiations, 'Aventine');
        const mostRecentAventineOffer = result ? { ...result.offer, round: result.round } : null;
        const offerData = {
          PortfolioOffer: newPortfolioOffer,
          ...(mostRecentAventineOffer && {
            Comments: mostRecentAventineOffer.Comments,
            Response: mostRecentAventineOffer.Response,
            OfferDate: mostRecentAventineOffer.OfferDate,
            Negotiator: mostRecentAventineOffer.Negotiator,
            UserType: mostRecentAventineOffer.UserType,
            CaseType: mostRecentAventineOffer.CaseType,
            LiveOffer: mostRecentAventineOffer.LiveOffer
          })
        };
        logger.log('🔍 [PROCESS ROW UPDATE] Updating Portfolio offer with complete data:', {
          parcelId: newRow.parcel_id,
          newPortfolioOffer,
          existingOffer: mostRecentAventineOffer,
          offerData
        });
        
        // Trigger real-time database update with complete offer information
        updateOfferWithDialog(
          newRow.parcel_id, 
          offerData, 
          getUpdateContext(),
          { updateFrontend: true },
          (result) => {
            logger.log('✅ Portfolio offer updated successfully:', result);
            setOffersChanged(true);
            
            // Clear user updates since the realtime update completed
            const rowId = newRow.id || newRow.parcel_id;
            clearUserUpdate(rowId, 'PortfolioOffer');
          },
          (error) => {
            logger.error('❌ Failed to update portfolio offer:', error);
            toast.error(`Failed to update portfolio offer: ${error.error}`);
          },
          townView ? null : handleShowDialog,
          { PortfolioOffer: oldRow.PortfolioOffer },
          apiRef,
          newRow // Pass current row data snapshot
        );
      }
    }
    
    // If TownOffer changed, the update is now handled directly in OfferEditCell
    // No additional processing needed here
    
    // Handle town-related field updates if in town view
    if (townView) {
      const caseIndex = getNegotiationObj.cases.findIndex(
        (caseItem) => caseItem.parcel_id === newRow.parcel_id
      );
      
      if (caseIndex !== -1) {
        const currentCase = getNegotiationObj.cases[caseIndex];
        let hasChanges = false;
        let updatedCase = { ...currentCase };
        
        
        // Track PortfolioRequestedAV changes
        if (newRow.PortfolioRequestedAV !== oldRow.PortfolioRequestedAV) {
          const newPortfolioRequestedAV = newRow.PortfolioRequestedAV === '' || newRow.PortfolioRequestedAV === 'None' ? null : Number(newRow.PortfolioRequestedAV);
          
          // Update current value in ref and check for changes
          const offerData = findOfferData(newRow.parcel_id);
          if (offerData) {
            const originalPortfolioRequestedAV = offerData.original.PortfolioRequestedAV;
            offerData.current.PortfolioRequestedAV = newPortfolioRequestedAV;
            
            // Log portfolio requested AV change if different from original
            if (originalPortfolioRequestedAV !== newPortfolioRequestedAV) {
              logger.log('Portfolio Requested AV Changed:', {
                ParcelID: offerData.ParcelID,
                field: 'PortfolioRequestedAV',
                oldValue: originalPortfolioRequestedAV,
                newValue: newPortfolioRequestedAV
              });
            }
          }
          
          updatedCase.PortfolioRequestedAV = newPortfolioRequestedAV;
          hasChanges = true;
        }
        
        if (newRow.TownResponse !== oldRow.TownResponse) {
          updatedCase.TownResponse = newRow.TownResponse;
          hasChanges = true;
        }
        
        
        if (newRow.TownNotes !== oldRow.TownNotes) {
          // Update current value in ref and check for changes
          const offerData = findOfferData(newRow.parcel_id);
          if (offerData) {
            const originalTownNotes = offerData.original.TownNotes;
            offerData.current.TownNotes = newRow.TownNotes;
            
            // Log town notes change if different from original
            if (originalTownNotes !== newRow.TownNotes) {
              logger.log('Town Notes Changed:', {
                ParcelID: offerData.ParcelID,
                field: 'TownNotes',
                oldValue: originalTownNotes,
                newValue: newRow.TownNotes
              });
            }
          }
          
          updatedCase.TownNotes = newRow.TownNotes;
          hasChanges = true;
        }
        
        if (hasChanges) {
          const updatedNegotiationObj = { ...getNegotiationObj };
          updatedNegotiationObj.cases[caseIndex] = updatedCase;
          setNegotiationObj(updatedNegotiationObj);
        }
      }
    }
    
    // Track changes for undo/redo if any offer fields were modified
    if (hasOfferFieldChanges) {
      // Track each field change individually
      if (newRow.OfferValue !== oldRow.OfferValue) {
        trackChange(newRow.parcel_id, 'OfferValue', oldRow.OfferValue, newRow.OfferValue);
      }
      if (newRow.TownOffer !== oldRow.TownOffer) {
        trackChange(newRow.parcel_id, 'TownOffer', oldRow.TownOffer, newRow.TownOffer);
      }
      if (newRow.PortfolioOffer !== oldRow.PortfolioOffer) {
        trackChange(newRow.parcel_id, 'PortfolioOffer', oldRow.PortfolioOffer, newRow.PortfolioOffer);
      }
      if (newRow.PortfolioRequestedAV !== oldRow.PortfolioRequestedAV) {
        trackChange(newRow.parcel_id, 'PortfolioRequestedAV', oldRow.PortfolioRequestedAV, newRow.PortfolioRequestedAV);
      }
      if (newRow.TownNotes !== oldRow.TownNotes) {
        trackChange(newRow.parcel_id, 'TownNotes', oldRow.TownNotes, newRow.TownNotes);
      }
    }
    
    return updatedRow;
  }, [updateNegotiationCase, getUpdateContext, getNegotiationObj, setNegotiationObj, townView, trackChange]);

  // Helper function to check if cell should be editable
  const shouldAllowEditing = React.useCallback((row) => {
    if (!row) return false;
    // Prevent editing if settled or awaiting SCAR decision
    // Note: We allow editing even when Negotiation is completed because sent offers should still be editable
    // The Negotiation completion status is for workflow tracking, not for preventing edits
    const notBarFiled = !row.MasterPetition && !row.barFiled;
    return !(row._isSettled || row.StipFinalized || row.AwaitingDecision || notBarFiled);
  }, []);

  // onCellClick handler to start edit for offer fields on single click
  const handleCellClick = React.useCallback((params) => {
    // Add null checks to prevent errors
    if (!params || !params.field || !params.id) {
      logger.log('Invalid params in handleCellClick:', params);
      return;
    }
    
    // Only for fields you want one-click edit
    if (params.field === 'OfferValue' || params.field === 'TownOffer') {
      if (townView && params.field === 'OfferValue') return;
      if (!shouldAllowEditing(params.row)) {
        return;
      }

      // 1) Move grid's focus to this cell
      apiRef.current.setCellFocus(params.id, params.field);

      // 2) Start edit mode on next paint
      requestAnimationFrame(() => {
        try {
          apiRef.current.startCellEditMode({ id: params.id, field: params.field });
        } catch (error) {
          logger.log('Error starting edit mode:', error.message);
        }
      });
    }
  }, [apiRef, shouldAllowEditing, townView]);

  // isCellEditable function to control cell editability at the DataGrid level
  const isCellEditable = React.useCallback((params) => {
    if (typeof params.field === 'string' && params.field.startsWith('Round')) {
      return false;
    }
    // Apply restrictions to offer fields
    if (params.field === 'OfferValue' || params.field === 'TownOffer') {
      if (townView && params.field === 'OfferValue') return false;
      return shouldAllowEditing(params.row);
    }
    // Notes use EditableNotesComponent popover only — not inline grid edit
    if (params.field === 'TownNotes') {
      return false;
    }
    // Allow editing for all other fields
    return true;
  }, [shouldAllowEditing, townView]);

  /** MUI GridRow → useGridRowAriaAttributes reads rowNode.id; rowNode can be null briefly after filter changes. */
  const renderGridRowSlot = React.useCallback(
    (rowProps) => {
      const { rowId } = rowProps;
      const row = rowProps.row;
      const rowNode = apiRef.current?.getRowNode?.(rowId) ?? null;
      if (!rowNode || !row) {
        return null;
      }
      const gridRow = <GridRow {...rowProps} />;
      if (townView || !row.MasterPetition) {
        return gridRow;
      }
      const title = row.CommAuth === 1
        ? 'Master Petition Case'
        : 'MP Filed case, NO AUTHORIZATION';
      return (
        <Tooltip title={title} placement="bottom" disableInteractive>
          {gridRow}
        </Tooltip>
      );
    },
    [apiRef, townView],
  );

  const adminTownTutorialScope = getNegotiationObj?.MuniCode && getNegotiationObj?.TaxYear
    ? `${getNegotiationObj.MuniCode}-${getNegotiationObj.TaxYear}`
    : null;

  return (
    <div className="h-full flex flex-col min-h-0 mt-2 flex-1">
      {townView && !user?.isLinkAuthenticated && (
        <TownViewTutorialOverlay
          ready={!loading && !isLoading && (getNegotiationObj?.cases?.length ?? 0) > 0}
          scopeKey={adminTownTutorialScope}
        />
      )}
      <VacateStipDialog />
      {/* Polling component - isolates polling logic to prevent table re-renders */}
      <SummaryCaseTablePollingComponent
        hasLoadingRows={hasLoadingRows}
        handleUpdateStateCallback={handleUpdateStateCallback}
        setNegotiationObjVersion={setNegotiationObjVersion}
        townView={townView}
      />
      {/* Development Toggle Buttons - only show when not in town view */}
      {/* {!townView && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Button
            variant={townView ? "contained" : "outlined"}
            color="primary"
            size="small"
            disabled
          >
            Town View: {townView ? 'ON' : 'OFF'} (via UserContext user.townView)
          </Button>
          <Button
            variant={activeStage === 'SCAR' ? "contained" : "outlined"}
            color="info"
            size="small"
            onClick={() => setActiveStage(activeStage === 'SCAR' ? 'BAR' : 'SCAR')}
          >
            Active Stage: {activeStage}
          </Button>
        </Box>
      )} */}

      <div className="flex flex-col flex-grow h-0 min-h-0 flex-1">
        <div className="flex-grow h-full flex flex-col min-h-0 overflow-hidden">
          {/* Don't show results for village cases (sql query on BE will have to be updated to handle village) */}
          {!townView && getNegotiationObj?.villageRun !== 1 && (
            <MunicipalResultsBanner />
          )}
          <div
            ref={gridShellRef}
            className="summary-case-sweep-scope bg-white w-full flex-1 min-h-0 h-full relative"
          >
            {showInitialLoadingOverlay && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  pointerEvents: 'none',
                }}
              >
                <CircularProgress size={36} />
                <Typography variant="body2" color="text.secondary">
                  Loading cases...
                </Typography>
              </Box>
            )}
            <DataGridPro
              apiRef={apiRef}
              initialState={{
                pagination: { paginationModel: { pageSize: 500, page: 0 } },
              }}
              density="compact"
              disableRowSelectionOnClick
              disableVirtualization={false}
              autoHeight={false}
              rowVirtualization
              disableColumnFilter={false}
              {...(townView
                ? {
                    filterModel: townFilterModel,
                    onFilterModelChange: handleTownCourtDateFilterModelChange,
                  }
                : {})}
              columnBufferPx={150}
              rowBufferPx={200}
              rows={dataGridRows}
              getRowId={(row) => row.id}
              columns={columns}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
              rowSelectionModel={selectionModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              disableMultipleColumnsSorting={false}
              checkboxSelection
              // Per-page selection when paginated; header checkbox targets the current page.
              checkboxSelectionVisibleOnly
              isRowSelectable={(params) => {
                // Allow selection of all cases including settled ones (previously didnt allow settled to be chosen)
                return true;
              }}
              pagination
              pageSize={pageSize}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              pageSizeOptions={[500, { value: -1, label: 'All' }, 25, 50, 100]}
              localeText={{
                noRowsLabel: duplicateRowIds.length
                  ? `Error: duplicate parcel IDs returned (${duplicateRowIds.join(', ')}). Table cannot be displayed.`
                  : (isLoading || showInitialLoadingOverlay) ? '' : 'No cases match criteria',
              }}
              slots={withSelectAllAwareCheckbox({
                toolbar: CustomToolbar,
                row: renderGridRowSlot,
                columnHeaderTitle: ({ label }) => (
                  <Box
                    className="MuiDataGrid-columnHeaderTitle"
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      fontWeight: 'var(--unstable_DataGrid-headWeight)',
                      lineHeight: 'normal',
                    }}
                  >
                    {label}
                  </Box>
                ),
              })}
              disableColumnMenu
              sx={{
                '& .MuiDataGrid-cell.photo-cell-no-padding': {
                  padding: '0 !important',
                  margin: '0 !important',
                },
                '& .MuiDataGrid-cell.photo-cell-no-padding > *': {
                  padding: '0 !important',
                  margin: '0 !important',
                },
                // Styling for settled rows - they are now selectable
                '& .MuiDataGrid-row.settled-row': {
                  '& .MuiDataGrid-cell': {
                    color: 'rgba(0, 0, 0, 0.38) !important',
                  },
                  '& .summary-status-awaiting-decision': {
                    color: '#000000 !important',
                  },
                },
                '& .MuiDataGrid-row.settled-row:not(.Mui-selected)': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02) !important',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02) !important',
                  },
                },
                // Master Petition rows - orange highlight
                '& .MuiDataGrid-row.master-petition-row': {
                  backgroundColor: '#fff !important',
                  boxShadow: 'inset 0 0 0 2px rgba(255, 152, 0, 0.5)',
                },
                // MP filed without commercial authorization - red border inside orange
                '& .MuiDataGrid-row.master-petition-row.master-petition-no-auth-row': {
                  boxShadow: 'inset 0 0 0 2px rgba(255, 152, 0, 0.5), inset 0 0 0 5px rgba(211, 47, 47, 0.75)',
                },
                // Remove background from disabled pagination arrows
                '& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled': {
                  backgroundColor: 'transparent !important',
                  '&:hover': {
                    backgroundColor: 'transparent !important',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(0, 0, 0, 0.26) !important'
                  }
                },
                '& .MuiDataGrid-footerContainer': {
                  display: 'flex',
                  alignItems: 'center',
                  py: 0,
                },
                '& .MuiTablePagination-root': {
                  overflow: 'visible',
                  width: '100%',
                },
                '& .MuiTablePagination-toolbar': {
                  minHeight: 48,
                  pl: 0.75,
                  pr: 0.75,
                  py: 0,
                  boxSizing: 'border-box',
                },
                '& .MuiTablePagination-spacer': { flex: 1 },
                '& .MuiTablePagination-displayedRows': { margin: 0, lineHeight: 1.35 },
                '& .MuiTablePagination-selectLabel': { margin: 0, lineHeight: 1.35 },
                '& .MuiTablePagination-actions': { marginLeft: 0.75 },
                '& .MuiTablePagination-actions .MuiIconButton-root': {
                  padding: '4px',
                },
              }}
              slotProps={{
                baseTooltip: { disableInteractive: true },
                toolbar: {
                  columnLayout,
                  villageMode,
                  fileName,
                  keys,
                  caseData: enhancedCaseData,
                  selectedRows,
                  rows: rows,
                  setSelectionModel,

                  onCancelOffers: handleCancelOffers,
                  offersChanged: offersChanged,
                  negotiationObjVersion,
                  setNegotiationObjVersion,
                  finalizing,
                  setFinalizing,
                  statusFilter, 
                  setStatusFilter: (value) => handleFilterChange(value, undefined), 
                  activeStage,
                  setActiveStage: (value) => handleFilterChange(undefined, value),

                  onAssignmentClick: handleAssignmentClick,
                  
                  // Props for Round columns toggle
                  showRoundColumns,
                  setShowRoundColumns,
                  maxRounds,
                  
                  // Props for AV toggle
                  avToggleActive,
                  setAvToggleActive,
                  
                  // Props for Generate Portfolio Offer button
                  getCompSheet,
                  getNegotiationObj,
                  village,
                  
                  // Props for selection management
                  setSelectionModel,
                  
                  // Props for undo/redo functionality - DISABLED
                  // canUndo,
                  // canRedo,
                  // onUndo: handleUndo,
                  // onRedo: handleRedo,
                  
                  // Props for pagination
                  pageSize,
                  setPageSize,
                  page,
                  setPage,
                  onScrollToRowId: handleScrollToRowId,
                  townCourtDateKeys,
                  townCourtDateFilterKey: townCourtDateFilterKey === UNASSIGNED_COURT_DATE && !hasUnassignedCourtDate
                    ? null
                    : townCourtDateFilterKey,
                  hasUnassignedCourtDate,
                  onTownCourtDateFilterChange: setTownCourtDateFilterOverride,
                },
                columnsManagement: {
                  getTogglableColumns: (columns) =>
                    columns
                      .map((c) => c.field)
                      // Remove the # comp index col, and the checkbox selection column.
                      .filter((field) => !String(field).startsWith('__') && field !== 'Comp'),
                },
              }}
              processRowUpdate={processRowUpdate}
              editMode="cell"
              isCellEditable={isCellEditable}
              getCellClassName={getCellClassName}
              onCellClick={handleCellClick}
              onCellDoubleClick={(params, event) => {
                const field = params.field;
                const preventEdit =
                  field === 'OfferValue' ||
                  field === 'TownOffer' ||
                  (typeof field === 'string' && field.startsWith('Round'));
                if (preventEdit) {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              getRowClassName={(params) => {
                const classes = [];
                const notBarFiled = !params.row.MasterPetition && !params.row.barFiled;
                if (params.row.notFiledAtScar && !params.row.MasterPetition) {
                  classes.push('not-filed-at-scar-row');
                }
                const cancellingThisRow =
                  townView &&
                  townCancellingParcelId != null &&
                  String(params.row.parcel_id) === String(townCancellingParcelId);
                if ((notBarFiled || params.row._isSettled || params.row.StipFinalized || params.row.AwaitingDecision) && !cancellingThisRow) {
                  classes.push('settled-row');
                  if (
                    townView &&
                    !notBarFiled &&
                    !params.row.AwaitingDecision &&
                    (params.row._isSettled || params.row.StipFinalized)
                  ) {
                    classes.push('town-view-settled-row');
                  }
                }
                if (!townView && params.row.MasterPetition) {
                  classes.push('master-petition-row');
                  if (params.row.CommAuth !== 1) {
                    classes.push('master-petition-no-auth-row');
                  }
                }
                return classes.join(' ');
              }}
              onError={(error) => {
                logger.warn('DataGrid error:', error);
                // Force a re-render if we encounter row-related errors
                if (error.message && error.message.includes('No row with id')) {
                  setNegotiationObjVersion(prev => prev + 1);
                }
              }}
              showToolbar
              disableRowSelectionExcludeModel />
          </div>
          {/* <ImpliedReductionStats rows={rows} townView={townView} /> */}
        </div>
      </div>

      {assignmentDialogOpen && (
        <DoNotFileScarDialog
          open={assignmentDialogOpen}
          onClose={() => setAssignmentDialogOpen(false)}
          selectedItems={selectedRows}
          isAdmin={isAdmin}
          onAssign={(selectedUserId, dueDate, flagData) => {
            // Update the negotiation object with the new jurisdictional status
            const updatedNegotiationObj = updateJurisdictionalAssignment(getNegotiationObj, flagData);
            
            // Always update the store for flag operations to ensure UI updates
            setNegotiationObj(updatedNegotiationObj);
            
            // Increment version to trigger re-renders
            setNegotiationObjVersion(prev => prev + 1);
          }}
          getNegotiationObj={getNegotiationObj}
        />
      )}

      {/* Offer Round Confirmation Dialog */}
      <OfferRoundConfirmationDialog
        open={offerRoundDialogOpen}
        onClose={handleDialogClose}
        onConfirm={offerRoundDialogData?.onConfirm}
        onCancel={offerRoundDialogData?.onCancel}
        onEditPriorRound={offerRoundDialogData?.onEditPriorRound}
        parcelId={offerRoundDialogData?.parcelId}
        newRoundNumber={offerRoundDialogData?.newRoundNumber}
        negotiationRounds={offerRoundDialogData?.negotiationRounds}
        offeredBy={offerRoundDialogData?.offeredBy}
        newOfferValue={offerRoundDialogData?.newOfferValue}
      />

      {/* Workup Viewer Dialog */}
      <WorkupViewerDialog
        open={workupViewerOpen}
        onClose={() => setWorkupViewerOpen(false)}
        files={workupViewerFiles}
        nyscefFiles={workupViewerNyscefFiles}
        address={workupViewerAddress}
        scarIndexNumber={workupViewerScarIndex}
        initialWorkupType={workupViewerType}
      />

      {/* EQ Warning Dialog */}
      <EQWarningDialog
        open={eqWarningDialogOpen}
        onClose={() => setEqWarningDialogOpen(false)}
        rar={getRar}
        eq={getEq}
      />
    </div>
  );
}

export default React.memo(SummaryCaseReviewTableRefactored); 